import type { LibraryCard } from './domain.js';

export const LIBRARY_CARD_SEEDS: LibraryCard[] = [
  {
    id: 'card-vent-termination',
    title: 'Vent termination clearance sweep',
    when: 'Before approving direct vent terminations near openings, corners, and walkways.',
    steps: [
      'Measure termination clearance from operable openings and grade.',
      'Check offset from forced-air intakes and meter/regulator locations.',
      'Confirm discharge path avoids habitual walking surfaces.',
      'Record any local amendment that overrides the baseline table.'
    ],
    fails: [
      'Termination too close to a window sash.',
      'Walkway discharge creates repeat nuisance conditions.',
      'Installer applied manufacturer notes without local amendment review.'
    ],
    refs: ['NFPA 54 §12.9', 'Termination clearance table', 'Local AHJ amendments'],
    tags: ['Venting', 'Safety'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-combustion-air-confined',
    title: 'Confined-space combustion air check',
    when: 'Any appliance room that feels undersized or houses multiple gas loads.',
    steps: [
      'Calculate room volume and total appliance input.',
      'Determine whether the space is confined by the 50 cu ft per 1000 BTU rule.',
      'If confined, verify opening strategy and louver free-area reduction.',
      'Capture calculations in the work notes before closeout.'
    ],
    fails: [
      'Forgetting the water heater load.',
      'Using nominal grille size instead of free area.',
      'No documentation of the actual volume calculation.'
    ],
    refs: ['NFPA 54 §9.3', 'NFPA 54 §9.4', 'Company combustion-air SOP'],
    tags: ['CombAir', 'Safety', 'Appliances'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-leak-test-procedure',
    title: 'Leak test before service release',
    when: 'After new piping work, valve replacement, or any opened gas path.',
    steps: [
      'Isolate the tested section and cap unused outlets.',
      'Apply the correct test medium and pressure for the system class.',
      'Hold for the required duration and watch for drift.',
      'If pressure decays, localize with approved leak solution and retest.'
    ],
    fails: [
      'Testing with fuel gas.',
      'Insufficient hold time.',
      'Gauge accuracy or range not appropriate for the test.'
    ],
    refs: ['NFPA 54 §8.1', 'Utility leak test SOP'],
    tags: ['LeakCheck', 'Testing', 'Safety'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-purge-procedure',
    title: 'Purge path control',
    when: 'Introducing gas to newly installed or empty piping systems.',
    steps: [
      'Verify the section passed leak test first.',
      'Open upstream control slowly and purge at the approved outlet point.',
      'Keep ignition sources and occupied spaces outside the release path.',
      'Document completion method and any abnormal observations.'
    ],
    fails: [
      'Purging into an occupied interior space.',
      'Weak ventilation plan.',
      'No designated purge observation point.'
    ],
    refs: ['NFPA 54 §8.3', 'Field purge checklist'],
    tags: ['Purge', 'Safety'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-regulator-vent',
    title: 'Regulator vent sanity pass',
    when: 'Any time a regulator is installed, reset, or inspected for lock-up issues.',
    steps: [
      'Verify the vent is unobstructed and not painted over.',
      'Check outlet direction or screened termination as required.',
      'Confirm clearance from openings and moisture/ice accumulation paths.',
      'Note any freeze, insect, or water-entry risk in the job notes.'
    ],
    fails: [
      'Vent termination inside a building envelope.',
      'Blocked bug screen.',
      'Condensate or roof runoff aimed at the vent.'
    ],
    refs: ['NFPA 54 §6.3.5', 'Manufacturer venting instructions'],
    tags: ['Regulators', 'Safety', 'Venting'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-meter-sizing',
    title: 'Meter load sizing check',
    when: 'Whenever appliances are added, restored, or questioned against current service capacity.',
    steps: [
      'Sum connected appliance loads in BTU/hr.',
      'Compare against service/meter capacity and regulator setting.',
      'Flag planned additions that push capacity near the margin.',
      'Record the comparison in notes or attach a load worksheet.'
    ],
    fails: [
      'Not counting future or secondary loads.',
      'Comparing against nominal, not effective, regulator setting.',
      'No proof artifact attached for capacity disputes.'
    ],
    refs: ['Manufacturer capacity charts', 'Field meter sizing worksheet'],
    tags: ['MeterSet', 'Load', 'Safety'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-odor-classification',
    title: 'Odor call classification quick pass',
    when: 'Use during leak or odor response when deciding severity, isolation, and documentation depth.',
    steps: [
      'Start with source control and survey boundaries before classification.',
      'Document occupancy, ignition hazards, and whether readings are rising, stable, or absent.',
      'Classify the condition using the utility response threshold and note why it fits.',
      'Capture the release or escalation decision before leaving the scene.'
    ],
    fails: [
      'Using odor alone without instrument context.',
      'Leaving out occupancy or ignition-source conditions.',
      'No written reason for downgrade or release.'
    ],
    refs: ['Utility odor-response SOP', 'NFPA 54 leak and testing sections'],
    tags: ['Odor', 'Safety', 'Testing'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-relight-sequence',
    title: 'Relight sequence and draft check',
    when: 'Any pilot relight, ignition restart, or post-outage appliance recommissioning visit.',
    steps: [
      'Verify appliance-specific lighting sequence before restoring gas.',
      'Observe ignition or pilot stability through the full startup cycle.',
      'Check vent draft or spill response after startup.',
      'Record final operation, shutdown, and customer instruction.'
    ],
    fails: [
      'Skipping manufacturer relight sequence.',
      'No draft observation after startup.',
      'Customer left without shutdown or relight instruction.'
    ],
    refs: ['Manufacturer lighting instructions', 'NFPA 54 appliance operation guidance'],
    tags: ['Relight', 'Appliances', 'Venting', 'Safety'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-appliance-connect',
    title: 'Appliance connector and sediment trap check',
    when: 'During appliance connection, reconnection, or shutdown-valve verification work.',
    steps: [
      'Verify shutoff accessibility and correct location relative to the appliance.',
      'Confirm approved connector length, routing, and protection from damage.',
      'Check union alignment and sediment trap placement where required.',
      'Run a leak check before cycling the appliance.'
    ],
    fails: [
      'Connector routed through walls or cabinets.',
      'Sediment trap missing where required.',
      'Shutoff not accessible after installation.'
    ],
    refs: ['NFPA 54 connector rules', 'Company appliance-connect SOP'],
    tags: ['Appliances', 'Piping', 'Safety', 'Code'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  },
  {
    id: 'card-csst-bonding',
    title: 'CSST bonding field check',
    when: 'Any appliance-connect, remodel, or leak response where CSST is present or suspected.',
    steps: [
      'Identify bonded vs unbonded CSST and trace the bonding path where visible.',
      'Verify clamp location and conductor continuity to the grounding electrode system.',
      'Note mixed materials, damaged jacket, or missing labels that raise uncertainty.',
      'Document any bonding defect before release and advise follow-up if required.'
    ],
    fails: [
      'Assuming equipment grounding counts as CSST bonding.',
      'Bond clamp installed on painted or insulated surfaces.',
      'No documentation when bonding is missing.'
    ],
    refs: ['Manufacturer bonding bulletin', 'Field bonding SOP'],
    tags: ['Bonding', 'Safety', 'Code', 'Piping'],
    favorite: false,
    system: true,
    updatedAt: new Date('2026-03-01T12:00:00.000Z').toISOString(),
    lastViewedAt: null
  }
];
